import { useState, useEffect } from 'react'
import { contactsAPI, contactGroupsAPI } from '../services/api'
import { Button, Card, Alert, Spinner, Modal, DataTable } from '../components/ui'
import { Formik, Form } from 'formik'
import { FormikInput, FormikSelect } from '../components/ui'
import * as Yup from 'yup'

const validationSchema = Yup.object().shape({
  name: Yup.string().nullable().max(255, 'Name must be at most 255 characters'),
  phone: Yup.string()
    .required('Phone number is required')
    .max(50, 'Phone number must be at most 50 characters'),
  contact_group_id: Yup.number().nullable(),
})

const ContactsPage = () => {
  const [contacts, setContacts] = useState([])
  const [contactGroups, setContactGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showAssignGroupModal, setShowAssignGroupModal] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const [importing, setImporting] = useState(false)
  const [selectedContacts, setSelectedContacts] = useState([])
  const [assigning, setAssigning] = useState(false)
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')

  // Load contact groups on mount
  useEffect(() => {
    loadContactGroups()
  }, [])

  // Load contacts on mount and when filters change
  useEffect(() => {
    loadContacts()
    // Clear selections when page changes
    setSelectedContacts([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentPage, perPage, sortBy, sortOrder])

  const loadContactGroups = async () => {
    try {
      setLoadingGroups(true)
      const response = await contactGroupsAPI.getAll({ per_page: 100 })
      if (response.data.success) {
        const data = response.data.data
        setContactGroups(data.contact_groups || [])
      }
    } catch (err) {
      console.error('Failed to load contact groups:', err)
    } finally {
      setLoadingGroups(false)
    }
  }

  const loadContacts = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        per_page: perPage,
        page: currentPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      }
      if (searchQuery) {
        params.search = searchQuery
      }

      const response = await contactsAPI.getAll(params)

      if (response.data.success) {
        const data = response.data.data
        setContacts(data.contacts || [])
        setPagination(data.pagination || pagination)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load contacts')
      setContacts([])
    } finally {
      setLoading(false)
    }
  }


  const handleCreate = () => {
    setEditingContact(null)
    setShowModal(true)
  }

  const handleEdit = (contact) => {
    setEditingContact(contact)
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return
    }

    try {
      setDeletingId(id)
      setError(null)
      const response = await contactsAPI.delete(id)

      if (response.data.success) {
        setSuccess('Contact deleted successfully')
        await loadContacts()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete contact')
      setTimeout(() => setError(null), 5000)
    } finally {
      setDeletingId(null)
    }
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setError(null)
      setSuccess(null)

      // Convert empty string to null for contact_group_id
      const submitValues = {
        ...values,
        contact_group_id: values.contact_group_id === '' ? null : (values.contact_group_id ? parseInt(values.contact_group_id) : null),
      }

      if (editingContact) {
        // Update
        const response = await contactsAPI.update(editingContact.id, submitValues)
        if (response.data.success) {
          setSuccess('Contact updated successfully')
          setShowModal(false)
          resetForm()
          await loadContacts()
          setTimeout(() => setSuccess(null), 3000)
        }
      } else {
        // Create
        const response = await contactsAPI.create(submitValues)
        if (response.data.success) {
          setSuccess('Contact created successfully')
          setShowModal(false)
          resetForm()
          await loadContacts()
          setTimeout(() => setSuccess(null), 3000)
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingContact ? 'update' : 'create'} contact`)
      setTimeout(() => setError(null), 5000)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const response = await contactsAPI.downloadTemplate()
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'contacts_import_template.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download template')
      setTimeout(() => setError(null), 5000)
    }
  }

  const handleImport = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      setImporting(true)
      setImportResult(null)
      setError(null)

      const response = await contactsAPI.import(file)

      if (response.data.success) {
        setImportResult(response.data.data)
        await loadContacts()
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import contacts')
      setImportResult(null)
    } finally {
      setImporting(false)
      e.target.value = '' // Reset file input
    }
  }

  const handleOpenImportModal = () => {
    setShowImportModal(true)
    setImportResult(null)
    setError(null)
  }

  const handleCloseImportModal = () => {
    setShowImportModal(false)
    setImportResult(null)
    setError(null)
  }

  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    )
  }

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length && contacts.length > 0) {
      setSelectedContacts([])
    } else {
      setSelectedContacts(contacts.map(contact => contact.id))
    }
  }

  const handleSort = (column, order) => {
    setSortBy(column)
    setSortOrder(order)
    setCurrentPage(1) // Reset to first page when sorting changes
  }

  const handlePageSizeChange = (newPerPage) => {
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset to first page when page size changes
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleBulkAssignGroup = async (values) => {
    if (selectedContacts.length === 0) return

    try {
      setAssigning(true)
      setError(null)
      setSuccess(null)

      // Convert empty string to null for contact_group_id
      const contactGroupId = values.contact_group_id === '' ? null : (values.contact_group_id ? parseInt(values.contact_group_id) : null)

      const response = await contactsAPI.bulkAssignGroup({
        contact_ids: selectedContacts,
        contact_group_id: contactGroupId,
      })

      if (response.data.success) {
        setSuccess(`Successfully assigned ${selectedContacts.length} contact(s) to group`)
        setShowAssignGroupModal(false)
        setSelectedContacts([])
        await loadContacts()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign contacts to group')
      setTimeout(() => setError(null), 5000)
    } finally {
      setAssigning(false)
    }
  }

  const handleExport = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {}
      if (searchQuery) {
        params.search = searchQuery
      }

      const response = await contactsAPI.export(params)

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `contacts_export_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      setSuccess('Contacts exported successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to export contacts')
      setTimeout(() => setError(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Contacts</h1>
        <p className="text-gray-600 mt-1">
          Manage your contacts.
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {success && (
        <div className="mb-4">
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      <Card>
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
            <div className="md:w-1/3">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                id="search"
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Search by name or phone..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              {selectedContacts.length > 0 && (
                <Button 
                  variant="primary" 
                  onClick={() => setShowAssignGroupModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Assign Group ({selectedContacts.length})
                </Button>
              )}
              <Button variant="secondary" onClick={handleOpenImportModal}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Import
              </Button>
              <Button variant="secondary" onClick={handleExport} disabled={loading}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
              <Button variant="primary" onClick={handleCreate}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Contact
              </Button>
            </div>
          </div>

          {contacts.length === 0 && !loading ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg font-medium mb-2">No contacts found</p>
              <p className="text-sm mb-4">Get started by adding your first contact</p>
              <Button variant="primary" onClick={handleCreate}>
                Add Contact
              </Button>
            </div>
          ) : (
            <DataTable
              columns={[
                {
                  key: 'name',
                  label: 'Name',
                  sortable: true,
                  render: (contact) => contact.name || <span className="text-gray-400 italic">-</span>,
                },
                {
                  key: 'phone',
                  label: 'Phone',
                  sortable: true,
                  render: (contact) => contact.phone,
                },
                {
                  key: 'contact_group_id',
                  label: 'Contact Group',
                  sortable: true,
                  render: (contact) => contact.contact_group?.name || <span className="text-gray-400 italic">-</span>,
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  sortable: false,
                  render: (contact) => (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEdit(contact)}
                        className="text-blue-600 hover:text-blue-900 font-medium transition-colors"
                      >
                        Edit
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        disabled={deletingId === contact.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 font-medium transition-colors"
                      >
                        {deletingId === contact.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  ),
                },
              ]}
              data={contacts}
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
              loading={loading}
              selectedItems={selectedContacts}
              onSelectAll={handleSelectAll}
              onSelectItem={handleSelectContact}
              perPage={perPage}
            />
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingContact(null)
        }}
        title={editingContact ? 'Edit Contact' : 'Add Contact'}
        size="lg"
      >
        <Formik
          initialValues={{
            name: editingContact?.name || '',
            phone: editingContact?.phone || '',
            contact_group_id: editingContact?.contact_group_id ? editingContact.contact_group_id.toString() : '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => {
            const contactGroupOptions = [
              { value: '', label: 'None' },
              ...contactGroups.map(group => ({
                value: group.id.toString(),
                label: group.name,
              })),
            ]

            return (
              <Form className="space-y-4">
                <FormikInput
                  id="name"
                  name="name"
                  label="Name"
                  placeholder="Enter contact name (optional)"
                />

                <FormikInput
                  id="phone"
                  name="phone"
                  label="Phone"
                  placeholder="Enter phone number"
                  required
                />

                <FormikSelect
                  id="contact_group_id"
                  name="contact_group_id"
                  label="Contact Group"
                  options={contactGroupOptions}
                  placeholder={loadingGroups ? "Loading contact groups..." : "Select a contact group (optional)"}
                  disabled={loadingGroups}
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowModal(false)
                      setEditingContact(null)
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {editingContact ? 'Update' : 'Create'}
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={handleCloseImportModal}
        title="Import Contacts"
        size="lg"
      >
        <div className="space-y-4">
          {/* Download Template Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-800 mb-1">Download Sample File</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Download the sample Excel file to see the correct format for importing contacts.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium underline flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download contacts_import_template.xlsx
                </button>
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div>
            <label htmlFor="import-file" className="block text-sm font-medium text-gray-700 mb-2">
              Upload Excel File
            </label>
            <input
              id="import-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              disabled={importing}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              Supported formats: .xlsx, .xls (Max size: 10MB)
            </p>
          </div>

          {/* Import Results */}
          {importing && (
            <div className="flex items-center justify-center py-4">
              <Spinner size="md" />
              <span className="ml-2 text-sm text-gray-600">Importing contacts...</span>
            </div>
          )}

          {importResult && (
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-800 mb-3">Import Results</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Rows:</span>
                  <span className="text-sm font-medium text-gray-900">{importResult.total_rows || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Imported:</span>
                  <span className="text-sm font-medium text-green-600">{importResult.imported || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Skipped:</span>
                  <span className="text-sm font-medium text-yellow-600">{importResult.skipped || 0}</span>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Errors:</p>
                    <div className="max-h-32 overflow-y-auto bg-red-50 border border-red-200 rounded p-2">
                      <ul className="text-xs text-red-700 space-y-1">
                        {importResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-4">
            <Button
              variant="secondary"
              onClick={handleCloseImportModal}
              disabled={importing}
            >
              {importResult ? 'Close' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assign Group Modal */}
      <Modal
        isOpen={showAssignGroupModal}
        onClose={() => {
          setShowAssignGroupModal(false)
        }}
        title={`Assign Group to ${selectedContacts.length} Contact(s)`}
        size="md"
      >
        <Formik
          initialValues={{
            contact_group_id: '',
          }}
          onSubmit={handleBulkAssignGroup}
        >
          {({ isSubmitting }) => {
            const contactGroupOptions = [
              { value: '', label: 'None (Remove from group)' },
              ...contactGroups.map(group => ({
                value: group.id.toString(),
                label: group.name,
              })),
            ]

            return (
              <Form className="space-y-4">
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Select a contact group to assign {selectedContacts.length} selected contact(s) to, or choose &quot;None&quot; to remove them from their current group.
                  </p>
                </div>

                <FormikSelect
                  id="contact_group_id"
                  name="contact_group_id"
                  label="Contact Group"
                  options={contactGroupOptions}
                  placeholder="Select a contact group (optional)"
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowAssignGroupModal(false)
                    }}
                    disabled={isSubmitting || assigning}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting || assigning}
                    disabled={isSubmitting || assigning}
                  >
                    Assign
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      </Modal>
    </div>
  )
}

export default ContactsPage

